import {InvoiceState} from "../../../libs/enum/invoice-state";
import {InvoiceType} from "../../../libs/enum/invoice-type";
import {InvoicePositionDto} from "./invoice-position.dto";

import {InvoiceVatDto} from "./invoice-vat.dto";
import {DeliveryNoteDto} from "../delivery-note.dto";
import {DocumentContactDto} from "../document-contact.dto";
import {isAfterToday, isDefined, isFalse, isNumeric, isTrue, isUuid, notDefined} from "../../../libs/utils/epic.utils";
import {DocumentPaymentDto} from "../document-payment.dto";
import {AccAmountPipe} from "../../../libs/pipes/amount.pipe";
import {SubInvoiceDto} from "../sub-invoice.dto";
import {ProformaInvoiceDto} from "../proforma-invoice.dto";
import {DocumentPropertyDto} from "../document-property.dto";

export class InvoiceDto implements AccAmountPipe {

  public id!: string;
  public readonly state!: InvoiceState;
  public type!: InvoiceType;
  public readonly number: string | undefined;
  public invoiceDate: Date | undefined;
  public orderDate: Date | undefined;
  public performanceDate: Date | undefined;
  public performanceDateTo: Date | undefined;
  public totalNetAmount!: number;
  public totalGrossAmount!: number;
  public readonly totalGrossAmountRounding: number | undefined;
  public readonly totalGrossAmountRounded: number | undefined;
  public totalVatAmount!: number;
  public currencyCode!: string;
  public fractionalDigits!: number;
  public readonly roundingValue: number | undefined;
  public readonly positions: InvoicePositionDto[] = [];
  public readonly vats: InvoiceVatDto[] = [];
  public readonly mediaId: string | undefined;
  public readonly deliveryNotes: DeliveryNoteDto[] = [];
  public readonly proformaInvoices: ProformaInvoiceDto[] = [];
  public readonly approved!: boolean;
  public readonly commentCount!: number;
  public readonly properties: DocumentPropertyDto[] = [];
  public readonly cancelled: boolean | undefined;
  public readonly cancelledById: string | undefined;
  public readonly cancelledByMediaId: string | undefined;
  public readonly cancellationOriginId: string | undefined;
  public readonly cancellationOriginMediaId: string | undefined;
  public contact: DocumentContactDto;
  public /* readonly */ payment: DocumentPaymentDto | undefined;
  public readonly deathId: string | undefined;

  constructor(init?: Partial<InvoiceDto> | undefined) {
    Object.assign(this, init);
    if (isDefined(init?.invoiceDate)) {
      this.invoiceDate = new Date(init.invoiceDate);
    }
    if (isDefined(init?.orderDate)) {
      this.orderDate = new Date(init.orderDate);
    }
    if (isDefined(init?.performanceDate)) {
      this.performanceDate = new Date(init.performanceDate);
    }
    if (isDefined(init?.performanceDateTo)) {
      this.performanceDateTo = new Date(init.performanceDateTo);
    }
    if (isDefined(init?.positions)) {
      this.positions = init.positions.map((i) => new InvoicePositionDto(i))
        .sort((i1, i2) => i1.pos < i2.pos ? -1 : i1.pos > i2.pos ? 1 : 0);
    } else {
      this.positions = [];
    }
    if (isDefined(init?.vats)) {
      this.vats = init.vats.map((i) => new InvoiceVatDto(i))
        .sort((i1, i2) => i1.vatRateValue < i2.vatRateValue ? -1 : i1.vatRateValue > i2.vatRateValue ? 1 : 0);
    } else {
      this.vats = [];
    }
    if (isDefined(init?.deliveryNotes)) {
      this.deliveryNotes = init.deliveryNotes.map((i) => new DeliveryNoteDto(i))
        .sort((i1, i2) => i1.created.getTime() > i2.created.getTime() ? -1 : i1.created.getTime() < i2.created.getTime() ? 1 : 0);
    } else {
      this.deliveryNotes = [];
    }
    if (isDefined(init?.proformaInvoices)) {
      this.proformaInvoices = init.proformaInvoices.map((i) => new ProformaInvoiceDto(i))
        .sort((i1, i2) => i1.created.getTime() > i2.created.getTime() ? -1 : i1.created.getTime() < i2.created.getTime() ? 1 : 0);
    } else {
      this.proformaInvoices = [];
    }
    if (isDefined(init?.properties)) {
      this.properties = init.properties.map((i) => new DocumentPropertyDto(i));
    } else {
      this.properties = [];
    }
    if (isDefined(init?.contact)) {
      this.contact = new DocumentContactDto(init.contact);
    } else {
      this.contact = new DocumentContactDto();
    }
    if (isDefined(init?.payment)) {
      this.payment = new DocumentPaymentDto(init.payment);
    }
  }

  public isDraft(): boolean {
    return notDefined(this.state) || this.state === InvoiceState.DRAFT;
  }

  public isOrder(): boolean {
    return isDefined(this.state) && this.state === InvoiceState.ORDER;
  }

  public isInvoice(): boolean {
    return isDefined(this.state) && this.state === InvoiceState.INVOICE;
  }

  public isCancellation(): boolean {
    return isDefined(this.state) && this.state === InvoiceState.CANCELLATION;
  }

  // Invoice that has already been canceled
  public isCancelled(): boolean {
    return isTrue(this.cancelled);
  }

  public isEditable(): boolean {
    return this.isDraft() || this.isOrder();
  }

  public isDeletable(): boolean {
    return this.isEditable();
  }

  public isCostEstimate(): boolean {
    return isDefined(this.state) && this.state === InvoiceState.COST_ESTIMATE;
  }

  public isAllowCreateOrder(): boolean {
    return this.isDraft() && isDefined(this.positions) && this.positions.length > 0;
  }

  public isAllowCreateInvoice(): boolean {
    return this.isOrder() && isDefined(this.positions) && this.positions.length > 0;
  }

  // Same rule as core (InvoiceService.changeState): the invoice is only issued on its date
  public isInvoiceDateInFuture(): boolean {
    return isAfterToday(this.invoiceDate);
  }

  // Same filter as core (InvoiceExchangePositionService): an article in another currency than the invoice
  public isExchangeRequired(): boolean {
    return this.positions.some((p) => isDefined(p.article) && p.article.currencyCode !== this.currencyCode);
  }

  public isAllowCreatePreview(): boolean {
    return (this.isDraft() || this.isOrder())
      && isUuid(this.id)
      && isDefined(this.positions) && this.positions.length > 0;
  }

  public isAllowCreateDeliveryNote(): boolean {
    return this.isOrder() || this.isInvoice() || this.isCostEstimate();
  }

  public hasPaymentBalance(): boolean {
    return isDefined(this.payment) && isFalse(this.payment.completed);
  }

  public getPaymentBalance(): number | undefined {
    if (this.hasPaymentBalance()) {
      return this.payment?.balance;
    }
    return undefined;
  }

  public hasPaymentEntries(): boolean {
    return isDefined(this.payment) && this.payment.entries.length > 0;
  }

  // used: CurrencyPipe / PaymentEnterComponent
  public getFractionalDigits(): number {
    if (isNumeric(this.fractionalDigits)) {
      return this.fractionalDigits;
    }
    return 2; // Fallback
  }

  // used: CurrencyPipe / PaymentEnterComponent
  public getPreferCurrencyCode(): string | undefined {
    return undefined;
  }

  // used: CurrencyPipe / PaymentEnterComponent
  public getCurrencyCode(): string {
    return this.currencyCode;
  }

  public isTypeVatDeclare(): boolean {
    return isDefined(this.type) && this.type === InvoiceType.VAT_DECLARE;
  }

  public isTypeNetWithoutVat(): boolean {
    return isDefined(this.type) && this.type === InvoiceType.NET_WITHOUT_VAT;
  }

  public isTypeGrossWithoutVat(): boolean {
    return isDefined(this.type) && this.type === InvoiceType.GROSS_WITHOUT_VAT;
  }

  public hasSubInvoices(): boolean {
    return this.deliveryNotes.length > 0 || this.proformaInvoices.length > 0;
  }

  public getSubInvoices(): SubInvoiceDto[] {
    return [...this.deliveryNotes, ...this.proformaInvoices]
      .sort((i1, i2) => i1.created.getTime() > i2.created.getTime() ? -1 : i1.created.getTime() < i2.created.getTime() ? 1 : 0);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public getSubInvoiceById(id: any): SubInvoiceDto | undefined {
    for (const idx of Array.isArray(id) ? id : [id]) {
      if (isUuid(idx)) {
        return this.deliveryNotes.find((d) => d.id === idx)
          || this.proformaInvoices.find((d) => d.id === idx);
      }
    }
    return undefined;
  }

  public clone(): InvoiceDto {
    return new InvoiceDto(this);
  }
}
